// WhatsApp utility functions for generating wa.me links with pre-filled messages

/**
 * Clean phone number - remove spaces, dashes, and ensure country code
 * @param {string} phone - Phone number
 * @param {string} countryCode - Default country code (Portugal = 351)
 * @returns {string} Cleaned phone number
 */
export const cleanPhoneNumber = (phone, countryCode = '351') => {
  if (!phone) return null;
  
  // Remove all non-numeric characters
  let cleaned = phone.replace(/\D/g, '');
  
  // If starts with 00, remove it
  if (cleaned.startsWith('00')) {
    cleaned = cleaned.substring(2);
  }
  
  // If starts with +, it's already handled by replace above
  
  // If it's a Portuguese number (9 digits starting with 9), add country code
  if (cleaned.length === 9 && cleaned.startsWith('9')) {
    cleaned = countryCode + cleaned;
  }
  
  // If it's already has country code (12 digits for Portugal)
  if (cleaned.length === 12 && cleaned.startsWith('351')) {
    return cleaned;
  }
  
  // If 9 digits, assume Portuguese and add code
  if (cleaned.length === 9) {
    cleaned = countryCode + cleaned;
  }
  
  return cleaned;
};

/**
 * Generate WhatsApp Web URL
 * @param {string} phone - Phone number
 * @param {string} message - Pre-filled message (optional)
 * @returns {string} WhatsApp URL
 */
export const getWhatsAppUrl = (phone, message = '') => {
  const cleanedPhone = cleanPhoneNumber(phone);
  if (!cleanedPhone) return null;
  
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanedPhone}${message ? `?text=${encodedMessage}` : ''}`;
};

/**
 * Open WhatsApp with pre-filled message
 * @param {string} phone - Phone number
 * @param {string} message - Pre-filled message
 */
export const openWhatsApp = (phone, message = '') => {
  const url = getWhatsAppUrl(phone, message);
  if (url) {
    window.open(url, '_blank');
  }
};

// Pre-defined message templates
export const WhatsAppTemplates = {
  // General greeting
  greeting: (clientName) => 
    `Olá ${clientName}! 👋\n\nAqui é da EP3D. Como posso ajudar?`,
  
  // Order confirmation
  orderConfirmation: (clientName, orderId, total) => 
    `Olá ${clientName}! 👋\n\n✅ *Confirmação de Encomenda*\n\n📋 Encomenda: #${orderId}\n💰 Total: ${total}€\n\nObrigado pela preferência!\nEP3D - Vendas`,
  
  // Order ready for delivery
  orderReady: (clientName, orderId) => 
    `Olá ${clientName}! 👋\n\n📦 A sua encomenda #${orderId} está pronta para entrega!\n\nEntre em contacto para combinarmos a entrega.\n\nEP3D - Vendas`,
  
  // Visit reminder
  visitReminder: (clientName, date, time) => 
    `Olá ${clientName}! 👋\n\n📅 *Lembrete de Visita*\n\nTemos uma visita agendada para:\n🗓️ ${date}\n🕐 ${time || 'A confirmar'}\n\nConfirma a sua disponibilidade?\n\nEP3D - Vendas`,
  
  // Route notification - visiting today
  visitingToday: (clientName) => 
    `Olá ${clientName}! 👋\n\n🚗 Estarei na sua zona hoje e gostaria de passar para uma visita rápida.\n\nTem disponibilidade?\n\nEP3D - Vendas`,
  
  // Follow up after visit
  followUpVisit: (clientName) => 
    `Olá ${clientName}! 👋\n\nObrigado pela visita de hoje! 🤝\n\nFico ao dispor para qualquer questão.\n\nEP3D - Vendas`,
  
  // Quote/Proposal
  proposalSent: (clientName, proposalId) => 
    `Olá ${clientName}! 👋\n\n📄 *Proposta Enviada*\n\nEnviei a proposta #${proposalId} conforme combinado.\n\nQualquer dúvida estou ao dispor!\n\nEP3D - Vendas`,
  
  // Payment reminder
  paymentReminder: (clientName, amount, dueDate) => 
    `Olá ${clientName}! 👋\n\n💳 *Lembrete de Pagamento*\n\nValor: ${amount}€\nVencimento: ${dueDate}\n\nQualquer questão estou ao dispor.\n\nEP3D - Vendas`,
  
  // New product notification
  newProduct: (clientName, productName) => 
    `Olá ${clientName}! 👋\n\n🆕 *Novidade!*\n\nTemos um novo produto que pode interessar: *${productName}*\n\nGostaria de saber mais?\n\nEP3D - Vendas`,
  
  // Thank you
  thankYou: (clientName) => 
    `Olá ${clientName}! 👋\n\nObrigado pela confiança! 🙏\n\nEstamos sempre ao dispor.\n\nEP3D - Vendas`,
    
  // Custom message
  custom: (clientName, message) => 
    `Olá ${clientName}! 👋\n\n${message}\n\nEP3D - Vendas`
};

// Template options for UI selectors
export const WhatsAppTemplateOptions = [
  { id: 'greeting', label: 'Saudação', icon: '👋' },
  { id: 'orderConfirmation', label: 'Confirmação de Encomenda', icon: '✅' },
  { id: 'orderReady', label: 'Encomenda Pronta', icon: '📦' },
  { id: 'visitReminder', label: 'Lembrete de Visita', icon: '📅' },
  { id: 'visitingToday', label: 'Visita Hoje', icon: '🚗' },
  { id: 'followUpVisit', label: 'Após Visita', icon: '🤝' },
  { id: 'proposalSent', label: 'Proposta Enviada', icon: '📄' },
  { id: 'paymentReminder', label: 'Lembrete Pagamento', icon: '💳' },
  { id: 'newProduct', label: 'Novo Produto', icon: '🆕' },
  { id: 'thankYou', label: 'Agradecimento', icon: '🙏' },
  { id: 'custom', label: 'Mensagem Personalizada', icon: '✏️' }
];
